import {
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import Pagination from '@/components/ui/Pagination'
import QueryState from '@/components/ui/QueryState'
import SearchInput from '@/components/ui/SearchInput'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useDictionary } from '@/features/courses/api/queries'
import type { DictionaryPage } from '@/schemas/courses'
import { toPageSize } from '@/schemas/table'
import DictionaryListSkeleton from '@/screens/courses/components/dictionary/DictionaryListSkeleton'
import DictionaryTermRow from '@/screens/courses/components/dictionary/DictionaryTermRow'
import { DICTIONARY_LIST } from '@/screens/courses/components/dictionary/dictionary-list-box'
import {
  DICTIONARY_SEARCH_DEFAULTS,
  dictionaryQuery,
  dictionarySearchSchema,
  loadDictionary,
} from '@/screens/courses/DictionaryScreen.loader'
import { DEFAULT_PAGE_SIZE } from '@/screens/courses/search'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'

const TITLE = 'Dictionary'

/**
 * The section tab strip names this page from `lg` up, so its `h1` is
 * screen-reader only there; below `lg` there is no strip and it shows.
 */
const HEADER = <TitleRow title={TITLE} />

function DictionaryPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading the dictionary…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <div className="flex flex-col gap-4">
        <Skeleton className="h-11 w-full rounded-md lg:h-9" />
        <DictionaryListSkeleton />
      </div>
    </ScreenLayout>
  )
}

function DictionaryError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} fullWidth header={HEADER} title="The dictionary did not load" />
}

export const Route = createFileRoute('/_app/learn/dictionary')({
  validateSearch: dictionarySearchSchema,
  search: { middlewares: [stripSearchParams(DICTIONARY_SEARCH_DEFAULTS)] },
  loader: loadDictionary,
  pendingComponent: DictionaryPending,
  errorComponent: DictionaryError,
  component: DictionaryScreen,
})

/**
 * The pager draws once there is more than one page, or once the member has
 * picked a size of their own.
 */
const showPager = (data: DictionaryPage) =>
  data.totalPages > 1 || data.pageSize !== DEFAULT_PAGE_SIZE

/**
 * Learn · Dictionary — the A–Z page: market terms, searched and paged
 * (`GET /dictionary/`). A word's own page is one click on a row.
 *
 * Keyed on `q`/`page`/`pageSize` (`dictionaryQuery`, shared with the loader
 * so a reload never keys the same search two ways) and read with
 * `useDictionary` (`keepPreviousData`), not suspense: typing keeps the
 * current results up (`isPlaceholderData`) instead of suspending the whole
 * route. (`FaqScreen` reads its list the same way, but grows it by "Load more"
 * rather than turning pages.)
 *
 * Typing is debounced 250ms into `?q=` with `replace` history, matching
 * `AllBrokersScreen`'s own hand-rolled debounce: `lastCommittedQuery` guards
 * the effect's mount run so opening `?q=kitta` does not immediately
 * re-navigate to the same URL, and `replace` keeps every keystroke off the
 * back-button stack — a member who typed five characters and hit Back
 * should land where they were before they searched, not on their own fourth
 * keystroke.
 */
function DictionaryScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const query = useDictionary(dictionaryQuery(search))

  const [term, setTerm] = useState(search.q ?? '')

  const lastCommittedQuery = useRef(search.q)
  useEffect(() => {
    const next = term.trim() === '' ? undefined : term
    if (next === lastCommittedQuery.current) return
    const id = setTimeout(() => {
      lastCommittedQuery.current = next
      void navigate({ search: (prev) => ({ ...prev, q: next, page: 1 }), replace: true })
    }, 250)
    return () => {
      clearTimeout(id)
    }
  }, [term, navigate])

  // `search.q` changing to something this component did not just commit
  // itself means the URL moved from outside the input — the sidebar's
  // Dictionary link, or Back to an earlier search — a search-only change
  // that never remounts the screen, so the box would otherwise keep showing
  // whatever was last typed into it.
  useEffect(() => {
    if (search.q === lastCommittedQuery.current) return
    lastCommittedQuery.current = search.q
    setTerm(search.q ?? '')
  }, [search.q])

  const goToPage = (page: number) => {
    void navigate({ search: (prev) => ({ ...prev, page }) })
  }

  const clearSearch = () => {
    setTerm('')
    lastCommittedQuery.current = undefined
    void navigate({ search: (prev) => ({ ...prev, q: undefined, page: 1 }), replace: true })
  }

  const isUpdating = query.isPlaceholderData

  return (
    <ScreenLayout {...LEARN_PAGE} header={HEADER} pageTitle={TITLE}>
      {/* 16 between the search and its list, the toolbar rhythm; the column's own 24 is for blocks. */}
      <div className="flex flex-col gap-4">
        <SearchInput
          aria-label="Search the dictionary"
          placeholder="Search the dictionary"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value)
          }}
        />

        <QueryState
          query={query}
          size="page"
          isEmpty={(data) => data.total === 0}
          skeleton={<DictionaryListSkeleton />}
          loadingLabel="Loading the dictionary…"
          empty={
            search.q === undefined
              ? {
                  title: 'The dictionary is being written',
                  description: 'Terms will show here as they are added.',
                }
              : {
                  kind: 'no-results',
                  title: `No terms match “${search.q}”`,
                  description: 'Try a different word, or clear the search.',
                  action: (
                    <button
                      type="button"
                      className={stateActionClass('page')}
                      onClick={clearSearch}
                    >
                      Clear search
                    </button>
                  ),
                }
          }
          failed={{ title: 'The dictionary did not load' }}
        >
          {(data) =>
            data.rows.length === 0 ? (
              // A stale `?page=` past the last page: mm-be answers it with no
              // rows and the real total.
              <EmptyState
                kind="no-results"
                size="page"
                title={`There is no page ${String(search.page)}`}
                description={`The dictionary ends on page ${String(data.totalPages)}.`}
                action={
                  <button
                    type="button"
                    className={stateActionClass('page')}
                    onClick={() => {
                      goToPage(data.totalPages)
                    }}
                  >
                    Go to page {data.totalPages}
                  </button>
                }
              />
            ) : (
              <div className="flex flex-col">
                <RefreshRegion updating={isUpdating} className={DICTIONARY_LIST}>
                  {data.rows.map((row) => (
                    <DictionaryTermRow key={row.slug} term={row} />
                  ))}
                </RefreshRegion>
                {showPager(data) ? (
                  <>
                    <Pagination
                      page={data.page}
                      pageSize={data.pageSize}
                      total={data.total}
                      unit="terms"
                      onPageChange={goToPage}
                      onPageSizeChange={(next) => {
                        void navigate({
                          search: (prev) => ({
                            ...prev,
                            pageSize: toPageSize(next, prev.pageSize),
                            page: 1,
                          }),
                        })
                      }}
                    />
                    <UpdatingNote when={isUpdating} label="Loading this page…" />
                  </>
                ) : null}
              </div>
            )
          }
        </QueryState>
      </div>
    </ScreenLayout>
  )
}
